import {ApiError, apiFetch, apiFetchBlob} from "./client";
import {apiErrorCode, messageForCodeWithDetails, portalInTermsMessages} from "./error-codes";

export type MerchantOrderStatus =
  | "NEW"
  | "PROCESS"
  | "APPEAL"
  | "CANCEL"
  | "OK";

export type MerchantOrderSide = "in" | "out";

export interface MerchantOrderConversion {
  /** Signed merchant balance impact, already formatted by the backend. */
  amount: string;
  currency: string;
  /** RUB paid per one unit of currency; already formatted by the backend. */
  rate: string;
  kind: "actual" | "estimated";
  /**
   * Оценка по нижней границе диапазона: пока трейдер не выбрал сумму, `amount` —
   * оценка по верхней. Нет поля — у заявки точная сумма или сумма уже выбрана.
   */
  amountMin?: string;
}

export interface MerchantOrderItem {
  payinStage?: "new" | "requisites_issued";
  orderId: string;
  remoteId: string;
  side: MerchantOrderSide;
  amount: string;
  paidAmount: string;
  remainingAmount: string;
  currency: string;
  status: MerchantOrderStatus;
  successfulParts: number;
  receiptCount: number;
  maxParts: number;
  conversion: MerchantOrderConversion | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Условия входящей заявки. Диапазон: `amount` до выбора — верхняя граница,
   * после выбора (`amountChosenAt`) — выбранная сумма, а границы остаются
   * пояснением. Переводы: сколько и минимум одного. У заявок без условий полей нет.
   */
  amountMin?: string;
  amountMax?: string;
  amountChosenAt?: string;
  transferCount?: number;
  minTransferAmount?: string;
}

export interface MerchantOrdersResponse {
  items: MerchantOrderItem[];
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset?: number | null;
}

export interface MerchantPayinRequisites {
  kind?: "card" | "sbp";
  cardMask: string;
  bankName: string;
  phone?: string;
  recipient?: string;
}

export interface MerchantOrderDetails extends MerchantOrderItem {
  clientCard?: string;
  clientPhone?: string;
  clientBank?: string;
  clientName?: string;
  payinRequisites?: MerchantPayinRequisites;
  receipts: MerchantReceiptItem[];
}

/**
 * Один чек заявки. Чеков у одной части бывает несколько — трейдер закрыл её
 * несколькими переводами (В-23), — и тогда `part` у них совпадает, а `amount`
 * (сумма всей части) повторяется. Складывать суммы по чекам нельзя; складывают
 * по частям.
 *
 * `cancellation` — чек, по которому трейдер отменил платёж. Денег по нему не
 * зачислено, поэтому `amount` нет, а `part` нумерует отменённые платежи
 * отдельно от успешных.
 */
export interface MerchantReceiptItem {
  receiptId: string;
  part: number;
  amount?: string;
  currency: "RUB";
  cancellation: boolean;
  createdAt: string;
}

export type MerchantTimelineKind =
  | "created"
  | "published"
  | "processing_started"
  | "requisites_ready"
  | "receipt_uploaded"
  | "part_payment_failed"
  | "part_payment_confirmed"
  | "part_requisites_replaced"
  | "partially_completed"
  | "returned_to_pool"
  | "appeal"
  | "cancelled"
  | "completed"
  | string;

export interface MerchantTimelineItem {
  kind: MerchantTimelineKind;
  status: MerchantOrderStatus;
  occurredAt: string;
  amount?: string;
  currency?: string;
}

export interface MerchantTimelineResponse {
  items: MerchantTimelineItem[];
}

export type MerchantMovementKind =
  | "payment"
  | "balance_reset"
  | "manual_topup"
  | "manual_withdrawal"
  | "merchant_withdrawal_reserve"
  | "merchant_withdrawal_release"
  | "blockchain_deposit"
  | "historical_correction"
  | string;

export interface MerchantMovementItem {
  id: string;
  kind: MerchantMovementKind;
  delta: string;
  balanceAfter: string | null;
  currency: string;
  remoteId?: string;
  // occurredAt — когда событие случилось у источника, createdAt — когда строку
  // записали в книгу. Список приходит в порядке createdAt: balanceAfter это
  // нарастающий итог, снятый в момент записи.
  occurredAt: string;
  createdAt: string;
}

export interface MerchantMovementsResponse {
  items: MerchantMovementItem[];
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset?: number | null;
}

export interface MerchantDebtSettlement {
  mode: "debt";
  debtLimit: string | null;
  limitExceeded: boolean;
  exceededBy: string;
}

export interface MerchantPrepaySettlement {
  mode: "prepay";
  overdraftAllowance: string;
  knownInFlight: string;
  availableToClaim: string;
  availabilityIsEstimate: boolean;
}

export type MerchantSettlement =
  | MerchantDebtSettlement
  | MerchantPrepaySettlement;

export interface MerchantOverviewResponse {
  merchant: {
    name: string;
  };
  balance: {
    amount: string;
    currency: string;
  };
  settlement: MerchantSettlement;
  recentOrders: MerchantOrderItem[];
  recentMovements: MerchantMovementItem[];
}

export interface MerchantOrdersQuery {
  remoteId?: string;
  status?: MerchantOrderStatus;
  side?: MerchantOrderSide;
  createdFrom?: string;
  createdTo?: string;
  /** Integer RUB kopecks; kept as a string in the browser. */
  minAmount?: string;
  /** Integer RUB kopecks; kept as a string in the browser. */
  maxAmount?: string;
  limit?: number;
  offset?: number;
}

export interface MerchantPageQuery {
  limit?: number;
  offset?: number;
}

function setTrimmed(
  params: URLSearchParams,
  name: string,
  value: string | undefined,
): void {
  const normalized = value?.trim();
  if (normalized) params.set(name, normalized);
}

export function serializeMerchantOrdersQuery(
  query: MerchantOrdersQuery = {},
): string {
  const params = new URLSearchParams();
  setTrimmed(params, "remoteId", query.remoteId);
  if (query.status) params.set("status", query.status);
  if (query.side) params.set("side", query.side);
  setTrimmed(params, "createdFrom", query.createdFrom);
  setTrimmed(params, "createdTo", query.createdTo);
  setTrimmed(params, "minAmount", query.minAmount);
  setTrimmed(params, "maxAmount", query.maxAmount);
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.offset != null) params.set("offset", String(query.offset));
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

function serializeMerchantOrdersExportQuery(
  query: MerchantOrdersQuery = {},
): string {
  return serializeMerchantOrdersQuery({
    remoteId: query.remoteId,
    status: query.status,
    side: query.side,
    createdFrom: query.createdFrom,
    createdTo: query.createdTo,
    minAmount: query.minAmount,
    maxAmount: query.maxAmount,
  });
}

function serializePageQuery(query: MerchantPageQuery = {}): string {
  const params = new URLSearchParams();
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.offset != null) params.set("offset", String(query.offset));
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

export function getMerchantOverview(
  token: string | null,
  signal?: AbortSignal,
): Promise<MerchantOverviewResponse> {
  return apiFetch<MerchantOverviewResponse>("/merchant/me/overview", token, {
    signal,
  });
}

export function listMerchantOrders(
  token: string | null,
  query: MerchantOrdersQuery = {},
  signal?: AbortSignal,
): Promise<MerchantOrdersResponse> {
  return apiFetch<MerchantOrdersResponse>(
    `/merchant/me/orders${serializeMerchantOrdersQuery(query)}`,
    token,
    {signal},
  );
}

export function exportMerchantOrders(
  token: string | null,
  query: MerchantOrdersQuery = {},
  signal?: AbortSignal,
): Promise<Blob> {
  return apiFetchBlob(
    `/merchant/me/orders/export${serializeMerchantOrdersExportQuery(query)}`,
    token,
    {signal},
  );
}

export function getMerchantOrder(
  token: string | null,
  orderId: string,
  signal?: AbortSignal,
): Promise<MerchantOrderDetails> {
  return apiFetch<MerchantOrderDetails>(
    `/merchant/me/orders/${encodeURIComponent(orderId)}`,
    token,
    {signal},
  );
}

export function getMerchantOrderTimeline(
  token: string | null,
  orderId: string,
  signal?: AbortSignal,
): Promise<MerchantTimelineResponse> {
  return apiFetch<MerchantTimelineResponse>(
    `/merchant/me/orders/${encodeURIComponent(orderId)}/timeline`,
    token,
    {signal},
  );
}

export function getMerchantReceipt(
  token: string | null,
  receiptId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return apiFetchBlob(
    `/merchant/me/receipts/${encodeURIComponent(receiptId)}`,
    token,
    {signal},
  );
}

export function getMerchantOrderReceipts(
  token: string | null,
  orderId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return apiFetchBlob(
    `/merchant/me/orders/${encodeURIComponent(orderId)}/receipts/download`,
    token,
    {signal},
  );
}

export function listMerchantBalanceMovements(
  token: string | null,
  query: MerchantPageQuery = {},
  signal?: AbortSignal,
): Promise<MerchantMovementsResponse> {
  return apiFetch<MerchantMovementsResponse>(
    `/merchant/me/balance/movements${serializePageQuery(query)}`,
    token,
    {signal},
  );
}

export interface MerchantCreateOrderBody {
  /** UUID, сгенерированный формой при открытии; из него сервер строит remote_id. */
  clientRequestId: string;
  /** Целые копейки. */
  amount: number;
  bank: "any" | "sber" | "tinkoff" | "sbp";
  clientCard?: string | null;
  clientPhone?: string | null;
  clientFio?: string | null;
  clientBank?: string | null;
}

export interface MerchantCreateOrderResponse {
  orderId: string;
}

/**
 * Создаёт заявку из кабинета. Повтор с тем же clientRequestId возвращает уже
 * существующую заявку, поэтому повторная отправка формы после ошибки сети или
 * 503 не плодит вторую заявку.
 */
export function createMerchantOrder(
  token: string | null,
  body: MerchantCreateOrderBody,
  signal?: AbortSignal,
): Promise<MerchantCreateOrderResponse> {
  return apiFetch<MerchantCreateOrderResponse>("/merchant/me/orders", token, {
    method: "POST",
    body: JSON.stringify(body),
    signal,
  });
}

/** Открытые заявки, взятые и ещё в пуле, временно занимают баланс — новая в него не влезла. */
function balanceInsufficientMessage(error: ApiError): string {
  let shortBy: unknown;
  try {
    shortBy = (JSON.parse(error.body) as {shortBy?: unknown} | null)?.shortBy;
  } catch {
    shortBy = undefined;
  }
  const lack = typeof shortBy === "string" && shortBy !== "" ? `: не хватает ${shortBy} USDT` : "";
  return `Недостаточно баланса${lack}. Открытые заявки занимают баланс, пока не исполнятся или не отменятся. Пополните баланс или дождитесь их закрытия.`;
}

/**
 * `context` разводит два экрана с разной 400-й: на списках это фильтры, в форме
 * создания заявки — поля формы. Остальные статусы одинаковы для обоих.
 */
export function formatMerchantApiError(
  error: unknown,
  context: "read" | "create" = "read",
): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 400:
        if (context === "create") {
          return messageForCodeWithDetails(error, portalInTermsMessages)
            ?? "Проверьте поля формы и попробуйте снова.";
        }
        return "Проверьте значения фильтров и попробуйте снова.";
      case 403:
        return "У этой учётной записи нет доступа к данным мерчанта.";
      case 404:
        return "Запрошенные данные не найдены.";
      case 409:
        if (apiErrorCode(error) === "merchant_balance_insufficient") {
          return balanceInsufficientMessage(error);
        }
        return "Такая заявка уже есть. Обновите страницу заявок.";
      case 429:
        return "Слишком много запросов. Обновите страницу чуть позже.";
      case 503:
        return "Курс временно недоступен. Попробуйте ещё раз через пару минут.";
      default:
        if (error.status >= 500) {
          return "Сервис временно недоступен. Последние успешные данные сохранены.";
        }
        return "Не удалось получить данные.";
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return "Не удалось получить данные.";
}
